import { useState } from 'react';
import type { MissionTask } from '../../types';
import { Icon } from '../../components/Icon';
import { tokens as fmt } from '../../lib/format';

const STATE: Record<MissionTask['state'], { label: string; color: string; icon: string }> = {
  queued: { label: 'In coda', color: 'var(--faint)', icon: 'clock' },
  waiting_gpu: { label: 'Aspetta il modello', color: 'var(--warn)', icon: 'hourglass' },
  running: { label: 'Al lavoro', color: 'var(--info)', icon: 'loader' },
  done: { label: 'Fatto', color: 'var(--ok)', icon: 'check' },
  failed: { label: 'Fermato', color: 'var(--danger)', icon: 'x' },
  cancelled: { label: 'Annullato', color: 'var(--faint)', icon: 'x' },
};

// Il piano della missione: i lavori in ordine, chi li fa, a che punto sono, cosa controllano.
export function PlanPanel({ tasks }: { tasks: MissionTask[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (tasks.length === 0) {
    return (
      <div className="t-caption faint" style={{ padding: 24, textAlign: 'center' }}>
        Il piano non c'è ancora: il PM lo sta preparando.
      </div>
    );
  }
  const title = (id: string | null) => tasks.find((t) => t.id === id)?.title;
  return (
    <ol
      style={{ listStyle: 'none', margin: 0, padding: '16px 20px 32px', display: 'grid', gap: 8 }}
    >
      {tasks.map((t, i) => {
        const st = STATE[t.state];
        const expanded = open === t.id;
        return (
          <li key={t.id} className="tool-card" style={{ padding: '10px 14px' }}>
            <button
              onClick={() => setOpen(expanded ? null : t.id)}
              aria-expanded={expanded}
              style={{
                width: '100%',
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--text)',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span className="t-mono-xs faint" style={{ width: 22 }}>
                {i + 1}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-strong" style={{ display: 'block' }}>
                  {t.title}
                </span>
                <span className="t-caption dim">
                  {t.agent}
                  {t.phase === 'preparation' ? ' · preparazione' : ''}
                  {t.reviews ? ` · controlla «${title(t.reviews) ?? ''}»` : ''}
                  {t.model ? ` · ${t.model}` : ''}
                </span>
              </span>
              {t.tokens > 0 && <span className="t-num dim">{fmt(t.tokens)} token</span>}
              <span
                className="t-caption"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: st.color }}
              >
                <Icon name={st.icon} size={13} />
                {st.label}
              </span>
            </button>
            {expanded && (
              <div className="t-small" style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{t.task}</div>
                {t.verify && (
                  <div className="dim">
                    <strong>Verifica:</strong> {t.verify}
                  </div>
                )}
                {(t.result || t.error) && (
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      background: 'var(--bg-2)',
                      borderRadius: 'var(--r-md)',
                      padding: 10,
                      color: t.error ? 'var(--danger)' : 'var(--text)',
                    }}
                  >
                    {t.error ?? t.result}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
